/**
 * Enrol: email entry -> code entry (no_device state). Renders zero
 * resident/request data (spec constraint on this screen). POST
 * /api/auth/code-request always returns {ok:true} (enumeration-safe), so
 * the code-sent notice is unconditional, never "we found your account".
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Card, FormField } from '@ecmb/ui';
import type { ApiClient } from '../api.js';
import { enrolCopy, sharedCopy } from '../copy.js';

/**
 * ⚠️ DELIBERATELY LOCAL, NOT IN client/copy.ts — and this is a fence, not a
 * style choice. `client/copy.ts` is owned by sibling board t788 in this
 * assembly, so board 789 must not edit it. These two strings belong beside
 * `enrolCopy.invalidCodeError` and should be folded in once t788 lands; until
 * then, shipping a 429 the UI reports as SUCCESS was the worse option.
 *
 * Wording rules they follow: name the wait, never blame the nurse, and never
 * imply her code was wrong — a throttled verify happens while she is holding a
 * perfectly valid code. The phone is the standing fallback (the phone never
 * closes), so it is offered rather than left implicit.
 *
 * ⛔ AND NO EM OR EN DASH IN EITHER — II-portal-#2 (T1723 M4). Both carried one
 * (`try again — or ring`, `still valid — please wait`), which is the only
 * punctuation this project bans in rendered copy, and these two were the portal
 * side of that leak. Commas carry the same pause. The ban is now enforced over
 * every string literal under `portal/src/client` by
 * `test/client/conformance.spec.tsx`, so a third one cannot arrive quietly.
 */
const TOO_MANY_REQUESTS_SEND =
  'Too many sign-in attempts for this email address just now. Please wait a few minutes and try ' +
  'again, or ring the office and we will sort it out.';

const TOO_MANY_REQUESTS_VERIFY =
  'Too many sign-in attempts for this email address just now. Your code is still valid, please ' +
  'wait a few minutes and enter it again, or ring the office.';

type DemoNurse = { email: string; displayName: string; homeName: string };

/** Groups the roster by home, preserving the server's home-then-nurse
 *  ordering, so the demo list reads as three homes rather than nine loose
 *  addresses. */
function groupByHome(nurses: DemoNurse[]): Array<[string, DemoNurse[]]> {
  const byHome = new Map<string, DemoNurse[]>();
  for (const n of nurses) {
    const existing = byHome.get(n.homeName);
    if (existing) existing.push(n);
    else byHome.set(n.homeName, [n]);
  }
  return [...byHome.entries()];
}

export type EnrolProps = {
  api: ApiClient;
  /** Optional note shown above the form. Today's only caller is Picker's
   *  unconditional "trouble with your PIN" route (`enrolCopy.pinTroubleNotice`).
   *  ⚠️ It used to be a `409 pin_reset` route asserting "that PIN was reset";
   *  board 786/1049 removed that response, and the note is now deliberately
   *  conditional because the client cannot know whether her PIN survived. */
  note?: string | null;
  /** Fires on a successful code-verify (200); caller re-fetches auth state
   *  and re-routes from there rather than this screen owning navigation. */
  onSignedIn: () => void;
};

type Step = 'email' | 'code';

export function Enrol({ api, note, onSignedIn }: EnrolProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Demo-only nurse roster. Stays empty on 404 (real mode) and on any
  // failure: this is a convenience, never a precondition for signing in.
  const [demoNurses, setDemoNurses] = useState<DemoNurse[]>([]);
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  /**
   * ⭐ T1749 M9 (DS-27) — THE TWO ERROR IDs ARE NO LONGER DECLARED HERE, and the
   * two constants that held them (`EMAIL_ERROR_ID` / `CODE_ERROR_ID`) are gone
   * rather than left beside a component that ignores them. `FormField` DERIVES
   * both from the control's own id (`errorIdFor`, `packages/ui/src/FormField.tsx`),
   * and the strings it derives are byte-identical to the two this screen used to
   * hand-write: `enrol-email` -> `enrol-email-error`, `enrol-code` ->
   * `enrol-code-error`. The rule the old comment stated ("one string each, so the
   * association cannot drift") is now structural instead of conventional — the id
   * on the control and the id on the message are the same expression.
   */

  /**
   * ⭐⭐ II-portal-#21 · ADR4-D10(b) (T1723 M4) — EVERY REFUSAL ON THIS SCREEN
   * MARKS ITS CONTROL AND MOVES TO IT.
   *
   * ⛔ D10(b) IS NOT A REQUEST-FORM RULE. It says a rejected input moves the user
   * to the problem, and this screen refuses four different ways (a throttled
   * send, a throttled verify, a wrong home device and a bad code) while doing
   * neither half: focus stayed on the button she pressed, and the input carried
   * no `aria-invalid`, so 1717's `[aria-invalid='true']` border could not reach
   * it either.
   * ⛔ `focus()`, not `scrollIntoView()` — `RequestForm`'s `focusFirstError`
   * shape: focus scrolls AND announces the field with its new state.
   * ⚠️ The ref belongs to the step that is on screen; a refusal never changes
   * step, so the control being focused is always the mounted one.
   *
   * ⛔⛔ AND SINCE T1749 M9 (DS-27) THIS `focus()` IS ALSO WHAT CARRIES THE
   * ANNOUNCEMENT. The message moved from a `p.field-error[role="alert"]` this
   * screen rendered by hand into `FormField`'s `error` slot, which deliberately
   * carries NO alert role (`FormField.tsx`'s own note: a per-field message is not
   * an interruption) and is announced through `aria-describedby` instead. An
   * `aria-describedby` message is only heard when something LANDS on the control
   * — so deleting the `ref.current?.focus()` line below would no longer merely
   * fail to move the nurse, it would make the refusal SILENT to a screen reader.
   * The three arms in `test/client/enrol.spec.tsx` grade focus, the mark and the
   * resolved message together for exactly this reason.
   */
  function refuse(message: string, ref: typeof emailRef, clear?: () => void): void {
    setError(message);
    clear?.();
    ref.current?.focus();
  }

  useEffect(() => {
    void (async () => {
      const res = await api.get<{ nurses: DemoNurse[] }>('/api/dev/nurses');
      if (res.kind !== 'response' || res.status !== 200) return;
      if (!Array.isArray(res.body?.nurses)) return;
      setDemoNurses(res.body.nurses);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendCode(): Promise<void> {
    if (busy || email.trim().length === 0) return;
    setBusy(true);
    setError(null);
    const res = await api.post('/api/auth/code-request', { email: email.trim() });
    setBusy(false);
    if (res.kind === 'network-error') {
      refuse(sharedCopy.networkError, emailRef);
      return;
    }
    /**
     * ⛔ A 429 MUST NOT RENDER AS SUCCESS — Captain gate 2. This function used to
     * fall straight through to `setSent(true); setStep('code')` after the
     * network check, so a throttled request told the nurse "we've sent you a
     * code". She then waits for an email that was never sent and retries,
     * spending more of the same budget. That made a throttle state
     * indistinguishable from normal operation — the same class as open Q65.
     */
    if (res.status === 429) {
      refuse(TOO_MANY_REQUESTS_SEND, emailRef);
      return;
    }
    /**
     * ⛔ NOR MAY ANY OTHER NON-200 RENDER AS SUCCESS — gate 4 advisory, and the
     * identical defect class to the 429 above. A 500 also fell through to
     * `setSent(true)`, so a server fault told the nurse a code was on its way.
     * Positive gate rather than another special case: only a 200 advances, so a
     * status nobody has thought of yet cannot claim success either.
     */
    if (res.status !== 200) {
      refuse(sharedCopy.networkError, emailRef);
      return;
    }
    setSent(true);
    setStep('code');
  }

  async function verifyCode(): Promise<void> {
    if (busy || code.trim().length === 0) return;
    setBusy(true);
    setError(null);
    const res = await api.post('/api/auth/code-verify', { email: email.trim(), code: code.trim() });
    setBusy(false);
    if (res.kind === 'network-error') {
      refuse(sharedCopy.networkError, codeRef);
      return;
    }
    if (res.status === 200) {
      onSignedIn();
      return;
    }
    if (res.status === 403) {
      refuse(enrolCopy.wrongHomeDeviceError, codeRef);
      return;
    }
    /**
     * ⛔ MUST PRECEDE THE invalidCode FALL-THROUGH — Captain gate 2. A 429 used
     * to land on `setError(invalidCodeError); setCode('')`, so a nurse holding a
     * PERFECTLY VALID code was told her code was wrong and had it cleared. The
     * code is not cleared here: it is still valid, and she will need it when the
     * window reopens.
     */
    if (res.status === 429) {
      refuse(TOO_MANY_REQUESTS_VERIFY, codeRef);
      return;
    }
    refuse(enrolCopy.invalidCodeError, codeRef, () => setCode(''));
  }

  function onEmailSubmit(e: JSX.TargetedEvent<HTMLFormElement, Event>): void {
    e.preventDefault();
    void sendCode();
  }

  function onCodeSubmit(e: JSX.TargetedEvent<HTMLFormElement, Event>): void {
    e.preventDefault();
    void verifyCode();
  }

  return (
    <div class="screen enrol-screen">
      <h1>{enrolCopy.title}</h1>
      {note && (
        <p class="notice" role="status">
          {note}
        </p>
      )}

      {/* ⭐ `shape="cover"` MATCHES THE KIOSK SIGN-IN (`quote-helper/src/client/screens/Login.tsx`).
          The two tools' sign-in screens are the pair a reviewer puts side by side and asks whether
          they read as one system, and a different card padding is exactly the difference she would
          have to name. Ruled at T1749 M9 (DS-27); `card` (the default) is what the first pass left. */}
      {step === 'email' && (
        <Card shape="cover">
          <form onSubmit={onEmailSubmit}>
            <FormField
              id="enrol-email"
              label={enrolCopy.emailLabel}
              type="email"
              autoComplete="email"
              placeholder={enrolCopy.emailPlaceholder}
              value={email}
              onInput={setEmail}
              required
              error={error ?? undefined}
              elementRef={emailRef}
            />
            <button type="submit" class="btn btn--primary" disabled={busy}>
              {enrolCopy.sendCodeButton}
            </button>
          </form>
        </Card>
      )}

      {step === 'email' && demoNurses.length > 0 && (
        <section class="demo-roster">
          <h2 class="demo-roster-title">{enrolCopy.demoRosterTitle}</h2>
          <p class="notice">{enrolCopy.demoRosterHint}</p>
          {groupByHome(demoNurses).map(([homeName, nurses]) => (
            <div key={homeName}>
              <p class="demo-roster-home-name">{homeName}</p>
              <ul class="demo-roster-list">
                {nurses.map((n) => (
                  <li key={n.email}>
                    <button type="button" class="btn btn--link" onClick={() => setEmail(n.email)}>
                      {n.displayName}{' '}
                      <span class="demo-roster-email">{n.email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      {step === 'code' && (
        <Card shape="cover">
          <form onSubmit={onCodeSubmit}>
            {sent && (
              <p class="notice" role="status">
                {enrolCopy.codeSentNotice}
              </p>
            )}
            {/* ⛔ `pattern` IS NOT DECORATION HERE, and it is the reason
                `FormField` gained the prop at T1749 M9 rather than the field
                losing it on adoption. Together with `required` and `maxLength`,
                `[0-9]{6}` makes the browser refuse anything that is not EXACTLY
                six digits BEFORE the POST, so a malformed code never spends one
                of this email address's rate-limited verify attempts — the budget
                whose exhaustion the two `TOO_MANY_REQUESTS_*` strings at the top
                of this file exist to explain.
                ⛔⛔ THE PATTERN WAS `[0-9]*` UNTIL M9 rework 1 AND THE SENTENCE
                ABOVE WAS FALSE OF IT (security-review). `[0-9]*` is a CHARACTER-SET
                guard, not a length guard: measured against the shipped attributes
                (`type=text pattern="[0-9]*" maxlength=6 required`), `""` was
                blocked by `required` but `"1"` and `"123"` were VALID and did
                submit, each one spending an attempt. The claim is made true here
                rather than softened, because the code is always six digits and
                `maxLength` already caps the other end, so the length floor costs
                nothing and genuinely protects the budget.
                ⚠️ `pattern` still does nothing at all on a field with no
                `required` — an EMPTY value is exempt from it by spec. This field
                has `required`; not every adopter does, and that is recorded in
                `FormField`'s own prop note rather than assumed here. */}
            <FormField
              id="enrol-code"
              label={enrolCopy.codeLabel}
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              placeholder={enrolCopy.codePlaceholder}
              value={code}
              onInput={setCode}
              required
              error={error ?? undefined}
              elementRef={codeRef}
            />
            <button type="submit" class="btn btn--primary" disabled={busy}>
              {enrolCopy.verifyButton}
            </button>
            <button
              type="button"
              class="btn btn--link"
              onClick={() => {
                setStep('email');
                setError(null);
                setCode('');
              }}
            >
              {enrolCopy.useDifferentEmailLink}
            </button>
            <button type="button" class="btn btn--link" onClick={() => void sendCode()} disabled={busy}>
              {enrolCopy.resendCodeLink}
            </button>
          </form>
        </Card>
      )}
    </div>
  );
}
